import {
  AddonsViewsNinjaLiveWidgetWidgetCallSummaryPeriodEnum,
  ApiKeysInSchema,
  LiveCallWidgetOutSchema,
} from "@/api/client";
import { handleError } from "@/api/handleError";
import { generateApiKey, getApiKey } from "@/api/requests/apiKeys";
import { getWidgetCallSummary } from "@/api/requests/liveWidget";
import { defineStore } from "pinia";
import { useBusinessStore } from "../businesses.store";
import { useUsersStore } from "../users.store";
import { useAddonStore } from "./index.store";

export interface WidgetData {
  id: number;
  business_name: string;
  archived: boolean | null;
  last_modified: string;
  date_created: string;
  logo: string | null;
  page_message: string | null;
  jumbotron: string | null;
  tool_tip_message: string | null;
  button_message: string | null;
  contacts: string | null;
  call_line: string | null;
  secondary_text_color: string;
  primary_text_color: string;
  bottom_position: number | null;
  right_position: number | null;
  primary_bg_color: string | null;
  secondary_bg_color: string | null;
  is_active: boolean;
  webhook_url: string | null;
  business:
    | {
        website: string | null;
      }
    | number
    | null
    | undefined;
  addon_subscription: number | null;
  dial_plan: string;
  website_type?: string | null;
}

interface LiveCallState {
  widgetTheme?: LiveCallWidgetOutSchema | null;
  gettingLiveCallMetrics: boolean;
  liveWidgetToken?: string | null;
  isFreeUser: boolean;
  isWidgetOnly: boolean;
  displayMode: boolean;
}

export const useLiveCallWidgetStore = defineStore("live-call", {
  state: (): LiveCallState => ({
    widgetTheme: null,
    gettingLiveCallMetrics: false,
    liveWidgetToken: null,
    isFreeUser: false,
    isWidgetOnly: false,
    displayMode: false,
  }),
  getters: {
    hasAccess(): boolean | undefined {
      const addonStore = useAddonStore();
      const userStore = useUsersStore();

      return (
        addonStore.getAllEnhancedAddons.some(
          (addon) =>
            addon.title?.toLowerCase().includes("website live call addon") &&
            addon.isPurchased
        ) ||
        userStore.currentUserBusinessLevel?.free_trial_data?.is_free_trial_user
      );
    },
  },

  actions: {
    async getMetrics(
      period: AddonsViewsNinjaLiveWidgetWidgetCallSummaryPeriodEnum,
      start?: string,
      end?: string
    ) {
      const businessStore = useBusinessStore();
      const businessId = businessStore.currentBusinessId;

      try {
        if (!businessId) {
          throw new Error("Business id not found");
        }

        this.gettingLiveCallMetrics = true;

        const getCallMetrics = await getWidgetCallSummary({
          period,
          endDate: end,
          startDate: start,
          businessId,
        });

        this.gettingLiveCallMetrics = false;

        return getCallMetrics.data;
      } catch (error) {
        handleError({
          error,
          fallbackMessage: "Get Call Metrics For Live Widget",
        });
      } finally {
        this.gettingLiveCallMetrics = false;
      }
    },
    async getCreateApiTokenForLiveWidget() {
      const businessStore = useBusinessStore();
      const businessId = businessStore.currentBusinessId;

      try {
        if (!businessId) {
          throw new Error("Business id not found");
        }

        const response = await getApiKey({ businessId });

        this.liveWidgetToken = response.data.public_key;

        if (!this.liveWidgetToken) {
          const payload: ApiKeysInSchema = {
            otp: "string",
            type: "public_key",
          };

          const createApiResponse = await generateApiKey({
            businessId,
            apiKeysInSchema: payload,
          });

          this.liveWidgetToken = createApiResponse.data.public_key;
        }
      } catch (error) {
        try {
          if (!this.liveWidgetToken) {
            if (!businessId) {
              throw new Error("Business id not found");
            }

            const payload: ApiKeysInSchema = {
              otp: "string",
              type: "public_key",
            };

            const createApiResponse = await generateApiKey({
              businessId,
              apiKeysInSchema: payload,
            });

            this.liveWidgetToken = createApiResponse.data.public_key;
          }
        } catch (createError) {
          handleError({
            error: createError,
            fallbackMessage: "Error during token creation in catch block",
          });
        }

        handleError({
          error,
          fallbackMessage: "Error during fetch/create LiveWidgetToken",
        });
      }
    },
  },
});
